import { normalizeLoadingPhaseText } from "./loadingPhaseCatalog";
import {
  publicPhaseDetailText,
  sideForNode,
} from "./loadingPhaseOrbitLayout";
import {
  STATUS_ACTIVE,
  buildProcessNodes,
  evidenceItemKey,
  isEvidenceMirroredNode,
  isPhaseMirrorNode,
  prioritizedEvidenceItemsFor,
  visibleProcessNodesFor,
} from "./loadingPhaseProcessModel";

const RESPONSE_GENERATION_PATTERN = /^(?:generating|preparing|finalizing|writing|assembling|composing|creating|constructing|synthesizing)\b.*\b(?:response|reply|answer|output)\b/i;

export const isResponseGenerationPhase = (phaseText = "") => (
  RESPONSE_GENERATION_PATTERN.test(normalizeLoadingPhaseText(phaseText))
);

export const buildLoadingPhaseViewModel = ({
  phaseText,
  phaseDetailText,
  phaseMeta,
  processQueue,
  useStackedNodes,
}) => {
  const processNodes = buildProcessNodes(processQueue);
  const phaseTitleText = String(phaseText || phaseMeta.detail || "Working").trim();
  const phaseDescriptionText = publicPhaseDetailText(phaseDetailText) || phaseMeta.description;
  const responseGenerationPhase = isResponseGenerationPhase(phaseTitleText);
  const centerEvidenceItems = responseGenerationPhase
    ? []
    : prioritizedEvidenceItemsFor(processNodes, 2);
  const centerEvidenceKeys = new Set(
    centerEvidenceItems.map((item) => evidenceItemKey(item)).filter(Boolean),
  );
  const sideProcessNodes = processNodes.filter((node) => (
    !isPhaseMirrorNode(node, phaseText) &&
    !isEvidenceMirroredNode(node, centerEvidenceKeys)
  ));
  const visibleProcessNodes = visibleProcessNodesFor(
    sideProcessNodes,
    useStackedNodes ? 4 : 5,
  );
  const evidenceHandoffLanes = centerEvidenceItems.map((item, evidenceIndex) => {
    const itemKey = evidenceItemKey(item);
    const sourceNode = processNodes.find((node) => {
      const nodeEvidenceItems = Array.isArray(node?.evidenceItems) ? node.evidenceItems : [];
      const nodeHasExactItem = itemKey && nodeEvidenceItems.some((candidate) => (
        evidenceItemKey(candidate) === itemKey
      ));
      return nodeHasExactItem || (item.stageKey && node.stageKey === item.stageKey);
    });
    const visibleSourceIndex = sourceNode
      ? visibleProcessNodes.findIndex((node) => node.key === sourceNode.key)
      : -1;
    const fallbackIndex = visibleSourceIndex >= 0
      ? visibleSourceIndex
      : visibleProcessNodes.findIndex((node) => (
        node.status === STATUS_ACTIVE &&
        Array.isArray(node?.evidenceItems) &&
        node.evidenceItems.length > 0
      ));
    const visualSourceNode = fallbackIndex >= 0 ? visibleProcessNodes[fallbackIndex] : sourceNode;

    if (!visualSourceNode && !sourceNode) return null;

    return {
      key: itemKey || `${visualSourceNode?.key || sourceNode.key}-${evidenceIndex}`,
      evidenceIndex,
      evidenceKey: itemKey,
      isVisibleSource: fallbackIndex >= 0,
      nodeIndex: fallbackIndex,
      nodeKey: visualSourceNode?.key || sourceNode.key,
      sourceKey: sourceNode.key,
      side: fallbackIndex >= 0 ? sideForNode(visualSourceNode, fallbackIndex) : 1,
      stageKey: sourceNode.stageKey,
      status: visualSourceNode?.status || sourceNode.status,
    };
  }).filter(Boolean).slice(0, 2);
  const evidenceHandoffByKey = new Map();
  evidenceHandoffLanes.forEach((lane) => {
    if (lane.evidenceKey) evidenceHandoffByKey.set(lane.evidenceKey, lane);
    evidenceHandoffByKey.set(`index-${lane.evidenceIndex}`, lane);
  });

  return {
    centerEvidenceItems,
    evidenceHandoffByKey,
    evidenceHandoffLanes,
    hasEvidenceItems: centerEvidenceItems.length > 0,
    phaseDescriptionText,
    phaseTitleText,
    processNodes,
    responseGenerationPhase,
    sideProcessNodes,
    visibleProcessNodes,
  };
};
